import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AnimatePresence, motion } from 'framer-motion';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Tasks from './pages/Tasks';
import Team from './pages/Team';
import Packages from './pages/Packages';
import Notifications from './pages/Notifications';
import Reports from './pages/Reports';
import Performance from './pages/Performance';
import Login from './pages/Login';
import MyTasks from './pages/MyTasks';

const PageWrapper = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="p-4 md:p-6"
    >
      {children}
    </motion.div>
  );
};

function AppContent() {
  const { user, isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-slate-950">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-slate-200 dark:border-slate-800 border-t-tg-orange-500 rounded-full animate-spin mx-auto mb-4 shadow-lg shadow-tg-orange-500/20"></div>
          <p className="text-slate-600 dark:text-slate-300 font-semibold">Loading TechnoGuide...</p>
          <p className="text-slate-400 dark:text-slate-600 text-sm mt-2 font-medium tracking-widest uppercase">Touch the Sky</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AnimatePresence mode="wait">
        <Login key="login-page" />
      </AnimatePresence>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar setSidebarOpen={setSidebarOpen} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto" onClick={() => setSidebarOpen(false)}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Navigate to={user?.role === 'Team Member' ? '/my-tasks' : '/dashboard'} replace />} />
              <Route path="/dashboard" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Dashboard /></PageWrapper>} />
              <Route path="/clients" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Clients /></PageWrapper>} />
              <Route path="/team" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Team /></PageWrapper>} />
              <Route path="/packages" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Packages /></PageWrapper>} />
              <Route path="/tasks" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Tasks /></PageWrapper>} />
              <Route path="/notifications" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Notifications /></PageWrapper>} />
              <Route path="/reports" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Reports /></PageWrapper>} />
              <Route path="/performance" element={user?.role === 'Team Member' ? <Navigate to="/my-tasks" replace /> : <PageWrapper><Performance /></PageWrapper>} />
              <Route path="/my-tasks" element={user?.role !== 'Team Member' ? <Navigate to="/dashboard" replace /> : <PageWrapper><MyTasks /></PageWrapper>} />
              <Route path="*" element={<Navigate to={user?.role === 'Team Member' ? '/my-tasks' : '/dashboard'} replace />} />
            </Routes>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;